import { create } from 'zustand';
import {
  consumeFirebaseRedirectResult,
  firebaseRegisterWithEmail,
  firebaseSignInWithEmail,
  firebaseSignInWithGoogle,
  firebaseSignOut,
  firebaseSendPasswordReset,
  getFirebaseAuth,
  isExternalProviderAvatar,
  subscribeAuth,
} from '../lib/firebase.js';
import { apiGet, apiPatch, apiPost } from '../lib/api.js';
import { mirrorAvatarFromUrl } from '../lib/avatar.js';
import i18n, { SUPPORTED_LANGUAGES, setLanguage, type SupportedLanguage } from '../i18n.js';
import { useSimulationStore } from './simulationStore.js';

export type AuthUser = {
  id: string;
  firebaseUid: string;
  email: string;
  name: string | null;
  avatar: string | null;
  role?: 'USER' | 'ADMIN';
  displayLanguage?: string | null;
  stripeCustomerId?: string | null;
  isCompany?: boolean;
  companyName?: string | null;
  companyNip?: string | null;
  companyAddressStreet?: string | null;
  companyAddressCity?: string | null;
  companyAddressZip?: string | null;
  companyAddressCountry?: string | null;
  invoiceEmail?: string | null;
  deletionScheduledAt?: string | null;
  deletionRequestedFrom?: string | null;
  /** Experimental call-style interview UI (admin opt-in). */
  experimentalInterview?: boolean;
  /** NULL until the welcome wizard is finished or skipped — while null the
   * app shell redirects every page to /welcome. */
  welcomeCompletedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

function applyRemoteLanguage(user: AuthUser) {
  const lang = user.displayLanguage?.slice(0, 2).toLowerCase();
  if (!lang) return;
  if (!(SUPPORTED_LANGUAGES as readonly string[]).includes(lang)) return;
  if (i18n.language?.slice(0, 2).toLowerCase() === lang) return;
  void i18n.changeLanguage(lang as SupportedLanguage);
}

/** Guards against concurrent mirror attempts (StrictMode double-invoke, overlapping syncs). */
let avatarMirrorInFlight = false;

/**
 * If the stored avatar still points at the identity provider's CDN
 * (e.g. lh3.googleusercontent.com), ask the API to copy it into our own
 * database so the app keeps a permanent URL that won't rotate or expire.
 * Runs at most once per user.
 */
async function mirrorProviderAvatarIfNeeded(
  user: AuthUser,
  set: (partial: Partial<AuthState>) => void,
): Promise<void> {
  if (avatarMirrorInFlight) return;
  if (!isExternalProviderAvatar(user.avatar)) return;

  avatarMirrorInFlight = true;
  try {
    const mirrored = await mirrorAvatarFromUrl(user.avatar!);
    if (mirrored) set({ user: mirrored });
  } catch (err) {
    // Non-fatal: keep the provider URL for now, retry on next sign-in.
    if (import.meta.env.DEV) console.warn('Avatar mirror failed:', err);
  } finally {
    avatarMirrorInFlight = false;
  }
}

export type AuthPack = {
  id: string;
  userId: string;
  type: string;
  totalSessions: number;
  usedSessions: number;
  stripePaymentId?: string | null;
  purchasedAt?: string;
  /** Present only for dynamic (PlanDefinition-based) subscription packs. */
  planDefinitionId?: string | null;
  billingInterval?: 'MONTHLY' | 'ANNUAL' | null;
  stripeSubscriptionId?: string | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  /** Set once cancellation is requested; access continues until currentPeriodEnd. */
  canceledAt?: string | null;
};

type AuthState = {
  user: AuthUser | null;
  pack: AuthPack | null;
  /** Gateable plan features in the user's plan; null until hydrated from the API. */
  features: string[] | null;
  /** Firebase email-verification state. Google accounts are always verified. */
  emailVerified: boolean;
  /** Primary Firebase sign-in provider id, e.g. 'password' or 'google.com'. */
  authProvider: string | null;
  isLoading: boolean;
  isInitialized: boolean;
  initializeAuth: () => void;
  /** Stops Firebase listener (call from App StrictMode cleanup / tests). */
  disposeAuthListener: () => void;
  syncWithBackend: () => Promise<void>;
  /** Lightweight refresh of user + pack from GET /auth/me (after sessions or purchases). */
  refreshProfile: () => Promise<void>;
  /** Persists that the welcome wizard was finished/skipped and updates the local user. */
  completeWelcome: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (name: string, email: string, password: string) => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  setDisplayLanguage: (lng: SupportedLanguage) => Promise<void>;
  /** Requests a fresh activation email for an unverified password account. */
  resendVerificationEmail: () => Promise<void>;
  /** Reloads the Firebase user + re-syncs; resolves to the new verified state. */
  reloadAndSync: () => Promise<boolean>;
};

/** Verification only applies to email/password accounts; Google is pre-verified. */
export function selectNeedsEmailVerification(state: AuthState): boolean {
  return Boolean(state.user) && !state.emailVerified && state.authProvider !== 'google.com';
}

/** Bumps on dispose so in-flight init after `consumeFirebaseRedirectResult` does not double-subscribe (React StrictMode). */
let authInitGeneration = 0;
let firebaseUnsub: (() => void) | null = null;

/** Coalesces overlapping `syncWithBackend` calls (e.g. auth listener + explicit sign-in). */
let syncInFlight: Promise<void> | null = null;
let refreshProfileInFlight: Promise<void> | null = null;

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  pack: null,
  features: null,
  emailVerified: false,
  authProvider: null,
  isLoading: true,
  isInitialized: false,

  initializeAuth: () => {
    const myGen = ++authInitGeneration;

    void (async () => {
      await consumeFirebaseRedirectResult();
      if (myGen !== authInitGeneration) return;

      firebaseUnsub?.();
      firebaseUnsub = subscribeAuth((fbUser) => {
        void (async () => {
          if (!fbUser) {
            set({
              user: null,
              pack: null,
              emailVerified: false,
              authProvider: null,
              isLoading: false,
              isInitialized: true,
            });
            return;
          }
          set({ isLoading: true });
          try {
            await get().syncWithBackend();
          } catch {
            await firebaseSignOut();
            set({ user: null, pack: null });
          } finally {
            set({ isLoading: false, isInitialized: true });
          }
        })();
      });
    })();
  },

  disposeAuthListener: () => {
    authInitGeneration += 1;
    firebaseUnsub?.();
    firebaseUnsub = null;
  },

  syncWithBackend: async () => {
    const fbUser = getFirebaseAuth().currentUser;
    if (!fbUser) return;

    // Wait out any in-flight sync first (e.g. the auth-listener's own call),
    // but don't short-circuit on it: that earlier call may have read stale
    // Firebase user fields (e.g. `displayName` before `updateProfile` resolved
    // during registration), so this call must still run with fresh data.
    if (syncInFlight) {
      await syncInFlight;
    }

    const body = {
      firebaseUid: fbUser.uid,
      email: fbUser.email ?? `${fbUser.uid}@users.noreply.interviewgator.localhost`,
      name: fbUser.displayName ?? null,
      avatar: fbUser.photoURL ?? null,
      // The UI language at sign-in time — the backend stores it on first sync
      // (registration) so transactional emails, the welcome email included,
      // arrive in the language the user actually browsed in.
      displayLanguage: i18n.language?.startsWith('pl') ? 'pl' : 'en',
    };

    const run = async () => {
      const data = await apiPost<{ user: AuthUser; pack: AuthPack | null; features?: string[] }>('/auth/sync', body, {
        forceAuthRefresh: true,
      });
      if (!data?.user) {
        throw new Error('Auth sync failed: server did not return a user.');
      }
      const nextUser = data.user;

      // On first Google sign-in the backend may have just stored the provider
      // URL on `User.avatar` — surface it to the UI so the mirror step below
      // has a source URL to pull from.
      if (!nextUser.avatar && fbUser.photoURL) {
        try {
          const patched = await apiPatch<{ user: AuthUser }>('/auth/me', {
            avatar: fbUser.photoURL,
          });
          if (patched?.user) Object.assign(nextUser, patched.user);
        } catch {
          /* non-fatal */
        }
      }

      const providerId = fbUser.providerData[0]?.providerId ?? null;
      set({
        user: nextUser,
        pack: data.pack ?? null,
        features: data.features ?? null,
        emailVerified: fbUser.emailVerified,
        authProvider: providerId,
      });
      applyRemoteLanguage(nextUser);

      // Copy provider-hosted avatar into our DB once, so the app owns a stable URL.
      void mirrorProviderAvatarIfNeeded(nextUser, set);
    };

    syncInFlight = run().finally(() => {
      syncInFlight = null;
    });
    await syncInFlight;
  },

  refreshProfile: async () => {
    if (!getFirebaseAuth().currentUser) return;

    if (refreshProfileInFlight) {
      await refreshProfileInFlight;
      return;
    }

    const run = async () => {
      const data = await apiGet<{ user: AuthUser; pack: AuthPack | null; features?: string[] }>('/auth/me');
      if (data?.user) {
        set({ user: data.user, pack: data.pack ?? null, features: data.features ?? null });
        applyRemoteLanguage(data.user);
      }
    };

    refreshProfileInFlight = run().finally(() => {
      refreshProfileInFlight = null;
    });
    await refreshProfileInFlight;
  },

  completeWelcome: async () => {
    const current = get().user;
    if (!current || current.welcomeCompletedAt) return;
    // Optimistic: unblock navigation immediately; the server stamp is the
    // source of truth on the next profile refresh.
    set({ user: { ...current, welcomeCompletedAt: new Date().toISOString() } });
    try {
      const data = await apiPost<{ user: AuthUser }>('/auth/welcome-complete', {});
      if (data?.user) set({ user: data.user });
    } catch {
      // Non-fatal: if persisting failed the user may see /welcome again next
      // session — better than blocking them now.
    }
  },

  signInWithGoogle: async () => {
    await firebaseSignInWithGoogle();
    // Popup path: currentUser is now set — sync immediately so callers can await it.
    // Redirect fallback path: the call above leaves the page; this line never runs.
    try {
      await get().syncWithBackend();
    } catch (err) {
      await firebaseSignOut();
      set({ user: null, pack: null });
      throw err;
    }
  },

  signInWithEmail: async (email, password) => {
    await firebaseSignInWithEmail(email, password);
    await get().syncWithBackend();
  },

  registerWithEmail: async (name, email, password) => {
    await firebaseRegisterWithEmail(name, email, password);
    await get().syncWithBackend();
  },

  sendPasswordReset: async (email) => {
    // Pass the current UI language so Firebase sends the reset email in the
    // language the user is actually browsing in (fixes Polish users receiving
    // an English reset email).
    await firebaseSendPasswordReset(email, i18n.language);
  },

  signOut: async () => {
    await firebaseSignOut();
    useSimulationStore.getState().reset();
    set({ user: null, pack: null, emailVerified: false, authProvider: null });
  },

  resendVerificationEmail: async () => {
    await apiPost('/auth/resend-verification', {});
  },

  reloadAndSync: async () => {
    const fbUser = getFirebaseAuth().currentUser;
    if (!fbUser) return false;
    // reload() refreshes emailVerified; syncWithBackend force-refreshes the ID
    // token so the API sees the updated email_verified claim.
    await fbUser.reload();
    await get().syncWithBackend();
    return get().emailVerified;
  },

  setDisplayLanguage: async (lng) => {
    setLanguage(lng);
    const { user } = get();
    if (!user) return;
    set({ user: { ...user, displayLanguage: lng } });
    try {
      const data = await apiPatch<{ user: AuthUser }>('/auth/me', { displayLanguage: lng });
      if (data?.user) set({ user: data.user });
    } catch {
      // keep optimistic update; toast already shown by api interceptor on real failures
    }
  },
}));


/**
 * Whether the current plan includes a gateable feature. `null` (not yet
 * hydrated from the API) counts as "has it" so UI never flashes locked states
 * while /auth/me is in flight; an empty array means the plan includes nothing.
 */
export function useHasFeature(feature: string): boolean {
  return useAuthStore((s) => s.features === null || s.features.includes(feature));
}
